import {
    useContext, useMemo, useState,
} from 'react';
import { DataGrid, GridColumnHeaderSortIcon, useGridApiRef } from '@mui/x-data-grid';
import {
    Box, Checkbox, FormControlLabel, IconButton, Link, MenuItem, TextField,
    Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import _ from 'lodash';

import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { useSelector } from 'BMapsRedux';
import { jobDefinitions, JobTypes } from 'BMapsSrc/jobs/JobDefinition';
import { CompoundHeritage } from 'BMapsModel';
import { updateLocalJobState } from 'BMapsRedux/jobs/access';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { HoverableCompoundStructureCell } from 'BMapsUI/tables/TableCells';
import { DialogCancelButton, DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import { wrapForDisabledTooltip } from 'BMapsUI/common/helpers';
import Select from 'BMapsUI/common/Select';
import { ProteinContext } from 'BMapsUI/common/ProteinContextProvider';
import ProteinSelect from 'BMapsUI/common/ProteinSelect';
import { setJobsCompoundsTableState } from 'BMapsRedux/projectState/access';
import { GridToolBar } from 'BMapsUI/tables/CustomToolbar';
import { CustomColumnsPanel } from 'BMapsUI/tables/CustomColumnsPanel';
import { MolDataSource } from 'BMapsSrc/utils';
import { getPropertyTooltip } from 'BMapsSrc/integrations/properties_rendering';
import {
    getSortComparatorForDirectionality, getSortingOrderForDirectionality,
} from 'BMapsSrc/integrations/properties_common';
import { getFormattedValue } from 'BMapsSrc/ui/tables/BMapsTable';
import { validateCompoundName } from 'BMapsSrc/util/mol_info_utils';
import { getPlacementOptions, PlacementTypes, getMoleculeOptions } from '../../tab_utils';
import { ImportErrors, InputBox } from '../../import_components';

export function JobsResultsTab({ closeImportPane, selected }) {
    const {
        localJobState, displayedCompoundJobs,
    } = useSelector((state) => state.jobs);

    let message; // content when no table to display
    if (!displayedCompoundJobs?.length) {
        message = (
            <span>
                No compound results to display.
                <br />
                Add results from the
                {' '}
                <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                    component="button"
                    onClick={() => UserActions.OpenImport('jobs_compounds_tab')}
                    sx={{ color: 'var(--marketing-blue)' }}
                >
                    Compound Jobs Tab
                </Link>
                {' '}
                to view them here.
            </span>
        );
    }

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden',
        }}
        >
            {!!selected && (
            <Typography
                component="div"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {message || (
                <CompoundJobGrid
                    closeImportPane={closeImportPane}
                    activeJobs={displayedCompoundJobs}
                    localJobState={localJobState}
                />
                )}
            </Typography>
            )}
        </Box>
    );
}

function CompoundJobGrid({
    closeImportPane, activeJobs, localJobState,
}) {
    const apiRef = useGridApiRef();
    const tableState = useSelector((state) => state.projectState.tableState.jobsCompoundsTable);
    const columnWidths = tableState?.columnWidths || {};
    const setColumnWidths = (widths) => {
        setJobsCompoundsTableState({
            columnWidths: widths,
        });
    };

    const compoundJobs = activeJobs.map((job) => (
        {
            job,
            structures: localJobState[`${job.jobid}`]?.structures || [],
        }
    ));

    const { selectedCaseData, selectedProtein } = useContext(ProteinContext);
    const [cmpdName, setCmpdName] = useState('');
    const [protein, setProtein] = useState({
        protein: selectedProtein,
        caseData: selectedCaseData,
    });
    const [checkedRowItems, setCheckedRowItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [continueAdding, setContinueAdding] = useState(false);
    const [loadErrors, setLoadErrors] = useState([]);
    const placementOptions = useMemo(() => {
        let placementFmt = 'smi'; // used to get placement options
        if (checkedRowItems.length > 0) {
            if (checkedRowItems.some(structHas3d)) placementFmt = 'mol';
        } else {
            if (compoundJobs.some((cj) => cj.structures.some(structHas3d))) placementFmt = 'mol';
        }
        const {
            availableOptions, disabledOptions,
        } = getPlacementOptions(placementFmt, protein.caseData);
        return [...availableOptions, ...disabledOptions];
    }, [protein, checkedRowItems]);
    const [placement, setPlacement] = useState(() => {
        const all3D = compoundJobs.length > 0 && compoundJobs.every(
            (cj) => cj.structures.length > 0 && cj.structures.every(structHas3d)
        );
        return all3D ? PlacementTypes.KeepExisting : PlacementTypes.PlaceOutside;
    });
    function findPlacement(value, placementOptionList) {
        return placementOptionList.find((option) => option.value === value);
    }
    function structHas3d(s) {
        return s.mol || s.sdf;
    }

    /**
     * @param {Array<Object>} importCmpdRows The selected compoound row objects to import
     */
    async function importMolecules(importCmpdRows) {
        const allErrors = [];
        const allWarnings = [];

        try {
            const placementOption = findPlacement(placement, placementOptions);
            if (!placementOption) {
                setLoadErrors(['Please choose a compound placement option.']);
                return;
            }

            setLoading(true);
            setLoadErrors([]); // Clear previous errors
            const { placementType, ligandspec: refSpec } = placementOption;
            const moleculeLoadOptions = getMoleculeOptions(placementType, true, refSpec || '', false);
            for (const importCmpdRow of importCmpdRows) {
                // Load each compound one at a time, along with properties and heritage
                let moldata = importCmpdRow.SMILES;
                let format = 'smi';
                if (importCmpdRow.sdf) {
                    moldata = importCmpdRow.sdf;
                    format = 'sdf';
                } else if (importCmpdRow.mol) {
                    moldata = importCmpdRow.mol;
                    format = 'mol';
                }
                const jobDefinition = jobDefinitions[importCmpdRow.jobtype];

                let molSource = new MolDataSource({
                    compoundName: cmpdName,
                    molFormat: format,
                    molData: moldata,
                });
                let addHeritage;

                // Special handling for certain jobs: molsource (load time) and heritage (post load)
                if (importCmpdRow.jobtype === JobTypes.REINVENT) {
                    molSource = MolDataSource.FromCompoundGenerationJob(cmpdName, format, moldata);
                    const inputInchi = importCmpdRow.Input_inchikey;
                    const loadedCompounds = App.Workspace.getLoadedCompounds();
                    const originalCompound = loadedCompounds.find((c) => c.getProperty('structure2d', 'inchikey') === inputInchi);
                    if (originalCompound) {
                        molSource.modifiedFrom = originalCompound.resSpec;
                    }
                    addHeritage = (cmpd) => {
                        const sourceCmpd = App.Workspace.getCompoundBySpec(molSource.modifiedFrom);
                        const heritageDetail = { ...importCmpdRow };
                        if (!sourceCmpd) {
                            heritageDetail.sourceParentId = importCmpdRow.Input_SMILES;
                        }
                        CompoundHeritage.addCompoundGeneration(sourceCmpd, cmpd, heritageDetail);
                    };
                }

                if (importCmpdRow.jobtype === JobTypes.GNINA_DOCKING) {
                    molSource.sourceType = MolDataSource.Types.GninaDocking;
                    addHeritage = (cmpd) => {
                        const heritageDetailObj = {
                            dockingProgram: 'gnina',
                            dockingScore: importCmpdRow.affinity,
                            gninaAffinity: importCmpdRow.cnn_affinity,
                            gninaPoseScore: importCmpdRow.cnn_score,
                            gninaVS: importCmpdRow.cnn_vs,
                        };
                        let sourceCmpd;
                        const sourceSpec = cmpd.getProperty('sdfProps', 'tmpSpec');
                        if (sourceSpec) {
                            sourceCmpd = App.Workspace.getCompoundBySpec(sourceSpec);
                        }
                        if (!sourceCmpd) {
                            heritageDetailObj.sourceParentId = importCmpdRow.SMILES;
                        }
                        CompoundHeritage.addDock(sourceCmpd, cmpd, heritageDetailObj);
                        // Clear temp properties added for loading
                        cmpd.scopedProperties.getPropertyRef('sdfProps', 'tmpI').clearValue();
                        cmpd.scopedProperties.getPropertyRef('sdfProps', 'tmpSpec').clearValue();
                    };
                }

                // Actually add to the workspace
                const { compounds: [compound], errors, warnings } = await UserActions.LoadMolData(
                    molSource, moleculeLoadOptions, protein?.caseData,
                    { skipHeritage: true },
                );

                // Collect errors and warnings
                if (errors && errors.length > 0) allErrors.push(...errors);
                if (warnings && warnings.length > 0) allWarnings.push(...warnings);

                // Add properties and heritage
                if (compound) {
                    const ignoreKeys = ['jobtype', 'jobid', 'id', 'SMILES', 'mol', 'sdf'];
                    const props = Object.keys(importCmpdRow).filter((k) => !ignoreKeys.includes(k));
                    const propertyScope = jobDefinition.propertyScope || jobDefinition.jobTypeId;
                    for (const property of props) {
                        compound.setProperty(propertyScope, property, importCmpdRow[property]);
                    }
                    if (addHeritage) addHeritage(compound);
                }
            }

            setCheckedRowItems([]);
            apiRef.current.setRowSelectionModel([]);
        } finally {
            setLoading(false);
        }

        const combinedErrors = [
            ...allErrors.map((err) => `Error: ${err}`),
            ...allWarnings.map((warn) => `Warning: ${warn}`),
        ];
        setLoadErrors(combinedErrors);
        if (combinedErrors.length === 0 && !continueAdding) {
            closeImportPane();
        }
    }

    function removeMolecules(rows) {
        const removedJobs = {};
        for (const row of rows) {
            const jobId = row.jobid;
            const jobDefinition = jobDefinitions[row.jobtype];
            const idField = jobDefinition.idField || 'id';
            const removedCompound = compoundJobs.flatMap((job) => job.structures)
                .find((compound) => compound[idField] === row.id);
            if (!removedCompound) {
                continue;
            }
            if (!removedJobs[jobId]) {
                removedJobs[jobId] = [];
            }
            removedJobs[jobId].push(removedCompound);
        }
        for (const [key, value] of Object.entries(removedJobs)) {
            const currentJobState = localJobState[key];
            const updatedJobState = currentJobState.structures.filter(
                (comp) => !value.includes(comp)
            );

            updateLocalJobState(key, { structures: updatedJobState });
        }
    }

    const { columns, rows } = buildCompoundGrid(
        compoundJobs, importMolecules, removeMolecules, columnWidths
    );

    const placementUIOptions = placementOptions
        .map((option) => {
            const {
                label, disabled, value, disabledMsg='', title='',
            } = option;
            const titleText = disabled ? disabledMsg : title;

            return (
                <MenuItem
                    key={label}
                    value={value}
                    disabled={disabled}
                    title={titleText}
                >
                    {label}
                </MenuItem>
            );
        });

    function handleColumnWidthChange(params) {
        const newWidths = { ...columnWidths, [params.colDef.field]: params.width };
        setColumnWidths(newWidths);
    }

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden',
        }}
        >
            <DialogContentSection
                title="Compound Job Results"
                style={{
                    display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden',
                }}
                contentStyle={{
                    display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
                }}
                disablePaddingBottom
            >
                <DataGrid
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    disableColumnMenu={false}
                    disableRowSelectionOnClick
                    initialState={{
                        density: 'compact',
                        pagination: {
                            paginationModel: { pageSize: 10, page: 0 },
                        },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    checkboxSelection
                    localeText={{
                        footerRowSelected: (count) => `${count} Compound${count !== 1 ? 's' : ''} selected`,
                    }}
                    onRowSelectionModelChange={(model) => {
                        const checkedRows = rows.filter((row) => model.ids.has(row.id));
                        setCheckedRowItems(checkedRows);
                    }}
                    onColumnVisibilityModelChange={(columnVisibilityModel) => {
                        setJobsCompoundsTableState({ columnVisibilityModel });
                    }}
                    columnVisibilityModel={tableState?.columnVisibilityModel
                    || getDefaultColumnVisibilityModel(compoundJobs)}
                    slots={{
                        toolbar: (toolBarProps) => (
                            <GridToolBar
                                {...toolBarProps}
                                exportFileName="BMapsJobsCompoundsExport"
                                toolBarId="JobsCompoundsTable"
                            />
                        ),
                        columnsPanel: CustomColumnsPanel,
                        columnHeaderSortIcon: (props) => {
                            const found = columns.find((c) => c.field === props.field);
                            const title = found ? `Sort by ${found.headerName}` : 'Sort';
                            return (
                                <BMapsTooltip title={title} noMaxWidth>
                                    <GridColumnHeaderSortIcon {...props} />
                                </BMapsTooltip>
                            );
                        },
                    }}
                    showToolbar
                    slotProps={{
                        basePopper: { placement: 'bottom-start' },
                    }}
                    onColumnWidthChange={handleColumnWidthChange}
                />
            </DialogContentSection>
            <DialogContentSection
                title="Load Options"
                style={{ display: 'flex', flexDirection: 'column' }}
                contentStyle={{ display: 'flex', flexDirection: 'column' }}
                defaultExpand
                collapsible
                disablePaddingBottom
            >
                <InputBox sx={{
                    display: 'flex', flexDirection: 'row', gap: '1rem', width: '100% !important',
                }}
                >
                    <ProteinSelect
                        onChange={(e, value) => setProtein(value)}
                        style={{ flexGrow: 1 }}
                    />
                    <TextField
                        fullWidth
                        name="molName"
                        label="Compound Prefix (optional)"
                        variant="outlined"
                        value={cmpdName}
                        onChange={(e) => {
                            const error = validateCompoundName(e.target.value);
                            if (error) {
                                setLoadErrors([error]);
                                return;
                            }
                            if (loadErrors.length) setLoadErrors([]);
                            setCmpdName(e.target.value);
                        }}
                        sx={{ flexGrow: 1 }}
                    />
                    <Select
                        name="placement"
                        label="Compound Placement"
                        slotProps={{ select: { native: false } }}
                        value={placement}
                        onChange={(e) => {
                            setPlacement(e.target.value);
                        }}
                        sx={{ flexGrow: 1 }}
                    >
                        {placementUIOptions}
                    </Select>
                </InputBox>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={continueAdding}
                            onChange={(e) => setContinueAdding(e.target.checked)}
                        />
                    )}
                    label="Continue adding compounds"
                />
            </DialogContentSection>
            <Box sx={{
                display: 'flex', flexDirection: 'row', gap: '1rem', width: '100%', paddingTop: '0.5rem',
            }}
            >
                <BMapsTooltip
                    title={checkedRowItems.length <= 0 ? 'Select a Compound Result to Load' : 'Load Selected Compound Results'}
                    style={{ flexGrow: 1 }}
                >
                    {wrapForDisabledTooltip(
                        <DialogPrimaryButton
                            type="button"
                            variant="contained"
                            sx={{ width: '100%' }}
                            onClick={async () => {
                                await importMolecules(checkedRowItems);
                            }}
                            disabled={checkedRowItems.length <= 0 || loading}
                        >
                            Load Compounds
                        </DialogPrimaryButton>
                    )}
                </BMapsTooltip>
                <BMapsTooltip
                    title={checkedRowItems.length <= 0 ? 'Select a Compound to Remove' : 'Remove Selected Compound Results'}
                    style={{ flexGrow: 1 }}
                >
                    {wrapForDisabledTooltip(
                        <DialogCancelButton
                            type="button"
                            sx={{ width: '100%' }}
                            onClick={() => {
                                removeMolecules(checkedRowItems);
                            }}
                            disabled={checkedRowItems.length <= 0 || loading}
                        >
                            Remove Compounds
                        </DialogCancelButton>
                    )}
                </BMapsTooltip>
            </Box>
            {loadErrors.length > 0 && <ImportErrors errors={loadErrors} />}
        </Box>
    );
}

function getDefaultColumnVisibilityModel(compoundJobs) {
    const columnVisibilityModelEntries = [];
    for (const { job: activeJob, structures } of compoundJobs) {
        const jobDefinition = jobDefinitions[activeJob?.jobtype];
        if (jobDefinition) {
            // Collect all property metadata keys, either explicitly defined or from structures
            let keys = new Set(Object.keys(jobDefinition.dataInfo || {}));
            for (const structure of structures) {
                for (const key of Object.keys(structure)) {
                    keys.add(key);
                }
            }
            keys = [...keys];
            // All metadata columns default to not visible unless otherwise stated
            columnVisibilityModelEntries.push(...keys
                .filter((key) => !jobDefinition.dataInfo[key]?.defaultVisible)
                .map((key) => [key, false])); // Preparing entry list for fromEntries below
        }
    }
    return (
        Object.fromEntries(columnVisibilityModelEntries)
    );
}

function buildCompoundGrid(compoundJobs, loadAction, removeAction, columnWidths) {
    const columns = [];
    let rows = [];
    function addOneColumn(key, propInfo={}) {
        if (propInfo.excludeFromTable) return;
        if (columns.find((col) => col.field === key)) return;
        const tooltip = dataInfoTooltip(propInfo);
        const columnInfo = {
            field: key,
            headerName: propInfo.label || key,
            title: propInfo.label || key,
            type: 'number',
            minWidth: 80,
            width: columnWidths[key],
            flex: columnWidths[key] ? undefined : 1,
            description: tooltip,
            renderCell: ({ value: propValue }) => (
                <BMapsTooltip title={propValue} placement="bottom-start">
                    <span>{getFormattedValue(propValue, propInfo.precision)}</span>
                </BMapsTooltip>
            ),
            renderHeader: () => (
                <BMapsTooltip title={tooltip} noMaxWidth>
                    <span>{propInfo.label || key}</span>
                </BMapsTooltip>
            ),
            sortComparator: getSortComparatorForDirectionality(propInfo.directionality),
            sortingOrder: getSortingOrderForDirectionality(propInfo.directionality),
        };
        if (propInfo.structureValue) {
            columnInfo.renderCell = ({ value, row }) => (
                <HoverableCompoundStructureCell smiles={value} uniqueId={row.id} compoundName={row['Compound Name']} />
            );
            columnInfo.sortable = false;
            columnInfo.filterable = false;
            columnInfo.type = 'string';
        }
        columns.push(columnInfo);
    }

    for (const { job: activeJob, structures } of compoundJobs) {
        const jobDefinition = jobDefinitions[activeJob?.jobtype];
        const knownColumns = jobDefinition?.dataInfo || {};
        const idField = jobDefinition?.idField || 'id';
        // Add explicitly defined columns
        for (const [key, propInfo] of Object.entries(knownColumns)) {
            addOneColumn(key, propInfo);
        }
        for (const structure of structures) {
            const tableRow = {
                ...structure,
                id: structure[idField],
                jobtype: activeJob.jobtype,
                jobid: activeJob.jobid,
            };
            rows.push(tableRow);
            // Add any extra columns that are not explicitly defined
            for (const key of Object.keys(structure)) {
                addOneColumn(key, knownColumns[key]);
            }
        }
    }
    columns.push({
        field: 'Actions',
        headerName: ' ',
        title: 'Compound Actions',
        flex: 1,
        minWidth: 75,
        sortable: false,
        filterable: false,
        hideable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => {
            const noun = 'Compound';
            return (
                <Box>
                    <BMapsTooltip title={`Remove ${noun}`}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                removeAction([row]);
                            }}
                        >
                            <ClearIcon fontSize="inherit" />
                        </IconButton>
                    </BMapsTooltip>
                </Box>
            );
        },
    });
    rows = _.uniqBy(rows, (r) => r.id)
        .filter((r) => r.id);
    if (!rows || rows.length === 0) {
        return { rows: [], columns: [] };
    }
    return { rows, columns };
}

export function dataInfoTooltip(dataInfo, heading) {
    if (!dataInfo) return null;
    let tooltip = dataInfo.label;
    const description = dataInfo.description;
    if (description) tooltip += `: ${description}`;
    if (Object.keys(dataInfo).length > 1) {
        const metaDataTooltip = getPropertyTooltip(heading, dataInfo);
        if (metaDataTooltip) tooltip = metaDataTooltip;
    }
    return tooltip;
}
